<template src="./template.html"></template>
<style src="./styles.scss" lang="scss"></style>

<script>
  import NewsItem from './NewsItem'
  import { removeWords } from '@/helper'
  import SearchFilter from './SearchFilter'
  import { omit, find, clone } from 'lodash'
  import { isMobile } from 'mobile-device-detect'
  import { mapActions, mapState, mapGetters } from 'vuex'
  import FiltersOverlay from '@/components/overlays/FiltersOverlay'
  import AppShowMoreLoader from '@/components/ui/AppShowMoreLoader'
  import SingleSelectInput from '@/components/ui/SingleSelectInput'
  import EditTopicOverlay from '@/components/overlays/EditTopicOverlay'
  import CreatePostOverlay from '@/components/overlays/CreatePostOverlay'
  import ConfirmationOverlay from '@/components/overlays/ConfirmationOverlay'
  import TopicFormOverlay from '@/components/overlays/group/TopicFormOverlay'

  export default {
    name: 'NewsStream',
    components: {
      NewsItem,
      SearchFilter,
      FiltersOverlay,
      EditTopicOverlay,
      TopicFormOverlay,
      CreatePostOverlay,
      AppShowMoreLoader,
      SingleSelectInput,
      ConfirmationOverlay,
    },
    props: {
      topicImg: String,
      renderFilters: Boolean,
      showActionItems: Boolean,
      renderCreatePost: Boolean,
      renderCreateTopic: Boolean,
      title: { type: String, default: null },
      groupId: { type: Number, default: null },
      topicId: { type: Number, default: null },
      type: { type: String, default: null },
      group: { type: Object, default: () => {} },
      topics: { type: Array, default: () => [] },
      isModule: { type: Boolean, default: false },
      isDashboardPage: { type: Boolean, default: false },
    },
    data() {
      return {
        page: 1,
        sub: null,
        hash: null,
        topicFilters: [],
        showFilters: false,
        isMobile: isMobile,
        selectedTopic: null,
        showCreatePost: false,
        selectedOverlay: null,
        showCreateTopic: false,
      }
    },
    computed: {
      ...mapState('news', [
        'news',
        'filters',
        'pagination',
        'filterActive',
        'filterOptions',
      ]),
      ...mapState('topics', ['topic']),
      ...mapGetters('groups', ['canManageTopic']),
      ...mapGetters('topics', [
        'canDeleteTopic',
        'canEditTopic',
      ]),
      showTitle() {
        return this.type === 'stream_widget' ? this.title : this.title || this.$t('news.title')
      },
      getDeleteTopicObject() {
        return {
          title: this.$t('topic.overlays.delete_topic.title'),
          img: this.topic && this.topic.infos && this.topic.infos.img,
          text: this.$t('topic.overlays.delete_topic.text'),
          button_text: this.$t('topic.overlays.delete_topic.button_text'),
        }
      },
      getFilteredTopics() {
        return this.topicFilters.filter(({ id }) => id !== this.selectedTopic?.id)
      },
      getFiltersCount() {
        return Object.values(this.filters).filter(v => v !== null).length
      },
    },
    watch: {
      '$route.query'(value) {
        this.page = value.page || 1

        if (this.filterActive) {
          this.filterNews(this.createParams(this.page))
        } else {
          this.getNews(this.createParams(this.page))
        }
      },
      selectedTopic: function(val) {
        if (val) {
          const newQuery = {
            ...this.$route.query,
            topic_id: val.id || '',
            page: 1,
          }
          const currentQuery = {
            topic_id: this.$route.query.topic_id || '',
            page: Number(this.$route.query.page || 1),
          }

          if (
            String(currentQuery.topic_id) !== String(newQuery.topic_id) ||
            Number(currentQuery.page) !== Number(newQuery.page)
          ) {
            this.$router.push({ query: newQuery })
          }

          this.requestTopic()
        } else {
          const rest = omit(this.$route.query, ['topic_id'])
          const newQuery = { ...rest, page: 1 }
          const currentQuery = {
            ...this.$route.query,
            topic_id: this.$route.query.topic_id || '',
            page: Number(this.$route.query.page || 1),
          }

          if (
            currentQuery.topic_id ||
            Number(currentQuery.page) !== 1
          ) {
            this.$router.push({ query: newQuery })
          }
        }
      },
    },
    created() {
      const topicIdFromQuery = this.$route.query.topic_id

      this.topicFilters = [
        {
          id: '',
          name: `${this.$t('topic.all_topics')} (${this.topics.length})`,
        },
        ...this.topics,
      ]

      if (this.topics.length === 1) {
        this.selectedTopic = this.topics[0]
      } else if (topicIdFromQuery) {
        const matchedTopic = this.topicFilters.find(t => String(t.id) === String(topicIdFromQuery))
        this.selectedTopic = matchedTopic || this.topicFilters[0]
      } else {
        this.selectedTopic = this.topicFilters[0]
      }

      this.page = this.$route.query.page || this.page
      this.paginateStream(1, this.page)
      this.hash = this.$route.hash
      this.sub = this.$store.subscribe(mutation => {
        switch (mutation.type) {
          case 'posts/CREATE_POST':
          case 'posts/DELETE_POST':
            this.getNews(this.createParams(this.page))
            break
          default:
            return false
        }
      })

      this.getNewsFilters()
    },
    mounted() {
      this.moveButton()
    },
    beforeDestroy() {
      // unsubscribe subscription
      this.sub()
      this.removeButton()
    },
    methods: {
      ...mapActions('news', [
        'getNews',
        'filterNews',
        'resetFilter',
        'updateFilters',
        'getTopicFilter',
        'getNewsFilters',
        'setFilterActive',
        'resetTopicFilter',
      ]),
      ...mapActions('groups', [
        'getGroup',
        'checkGroup',
      ]),
      ...mapActions('topics', [
        'getTopic',
        'editTopic',
        'checkTopic',
        'createTopic',
        'deleteTopic',
        'createTopicSuggestion',
      ]),
      ...mapActions('posts', [ 'getOptionsForTopic' ]),
      onFilter(page = 1) {
        this.setFilterActive()
        if (parseInt(this.$route.query.page) !== parseInt(page)) {
          this.$router.push({ query: { page } })
        } else if (
          this.pagination.last < this.pagination.self &&
          parseInt(this.$route.query.page) !== this.pagination.first
        ) {
          this.$router.push({ query: { page: this.pagination.first } })
        } else {
          this.filterNews(this.createParams(page))
        }
      },
      onUpdateFilter(args) {
        if (args) {
          this.selectedTopic = args
        }
      },
      paginateStream(start, end) {
        const hash = this.$route.hash
        this.getNews(this.createParams(start)).then(_ => {
          if (hash) {
            if (start + 1 <= end) {
              this.paginateStream(start + 1, end)
            } else {
              this.jumpToPosition(hash)
            }
          }
        })
      },
      updateNews(page = 1) {
        this.getNews(this.createParams(page))
      },
      createParams(page) {
        if (this.isDashboardPage) {
          document.querySelector('body').classList.add('page_dashboard')
        }

        const params = {}
        if (this.groupId) {
          params['group_id'] = this.groupId
        }
        if (this.selectedTopic && this.selectedTopic.id) {
          params['topic_id'] = this.selectedTopic.id
        }
        params['page'] = page
        return params
      },
      jumpToPosition(postId) {
        // Only do stuff, if hash is in expected structure
        const jumpTrigger = [
          '#jump-',
          '#comments-',
          '#likes-',
        ]

        const triggerMap = {
          '#comments-': '.comment-count',
          '#likes-': '.like-count',
        }

        const trigger = jumpTrigger.find(trigger => postId.includes(trigger))
        const clickTrigger = triggerMap[trigger]

        if (trigger) {
          // WIP - Have not handled the comments pagination scenario but when we have to jump to exact comment then assign arrayIds.join('-') to some variable and pass that in removeWords instead of postId and handle the pagination scenario.
          // Here I am spliting the combination of postId and commentId to have PostId and jumping to post comment only
          const arrayIds = postId.split('-')

          if (arrayIds.length === 4) {
            arrayIds.pop()
            postId = arrayIds.join('-')
          }
          if (
              // If we don't find plain Id in news, we have to paginate and call the function recursively
              !find(this.news, function(newsItem) {
                return newsItem.id === removeWords(postId, jumpTrigger)
              })
            ) {
            if (this.pagination.next) {
              this.getNews(this.createParams(this.pagination.next)).then(_ => {
                this.jumpToPosition(postId)
              })
            }
          } else {
              // Otherwise we jump to item and highlight it if hash is #jump-
            const jumpTriggerDomInterval = setInterval(
                () => {
                  const item = document.querySelector(postId)
                  if (item) {
                    const highlightItem =
                      item.classList.contains('news-item')
                        ? item
                        : item.closest('.news-item')
                    highlightItem.scrollIntoView()
                    highlightItem.classList.add('highlighted-post')
                    if (clickTrigger && item.querySelector(clickTrigger)) {
                      item.querySelector(clickTrigger).querySelector('.count').click()
                    }
                    clearInterval(jumpTriggerDomInterval)
                  }
                },
                500,
            )
          }
        }
      },
      triggerEditPost(newsItem) {
        const groupId = newsItem && newsItem.meta && newsItem.meta.group && newsItem.meta.group.id
        const topicId = newsItem && newsItem.meta && newsItem.meta.topic && newsItem.meta.topic.id

        return `${JSON.stringify(newsItem.attachments)}-${JSON.stringify(groupId)}-${JSON.stringify(topicId)}`
      },
      closeOverlay() {
        this.selectedOverlay = null
      },
      openOverlay(overlay) {
        if (this.topic) {
          this.checkTopic(this.topic.id).then(status => {
            if (status && status === -1) {
              return false
            }
            this.selectedOverlay = overlay
          })
        }
      },
      openCreateTopicOverlay() {
        this.checkGroup(this.$route.params.id).then(status => {
          if (status && status === -1) {
            return false
          }
          this.showCreateTopic = true
        })
      },
      requestTopic() {
        this.getTopic(this.selectedTopic.id).then(_ => {
          if (this.topic && this.topic.group) {
            this.getOptionsForTopic(this.topic.group.id)
          }
        })
      },
      submitTopic(args) {
        const topic = {
          name: args.name,
          group_id: this.group.id,
          description: args.description,
        }
        if (args.type === 'create') {
          this.createTopic(topic).then(_ => {
            this.getGroup(this.$route.params.id)
            this.showCreateTopic = false
          })
        } else {
          this.createTopicSuggestion(topic).then(_ => {
            this.getGroup(this.$route.params.id)
            this.closeOverlay()
          })
        }
      },
      handleEditTopic(args) {
        const topic_id = this.topic.id
        const group_id = this.$route.params.id
        const title = args.title
        const description = args.description
        this.editTopic({ id: topic_id, payload: { group_id, title, description } }).then(_ => {
          this.closeOverlay()
          this.getGroup(this.$route.params.id)
        })
      },
      actionDeleteTopic() {
        if (this.topic) {
          this.deleteTopic(this.topic.id).then(_ => {
            if (this.topic.group) {
              this.$router.replace({ name: 'group', params: { id: this.topic.group.id } })
            }
          })
        }
      },
      onFilterChange(filter) {
        if (filter && filter.types && filter.types.key !== 'post') {
          this.getTopicFilter(filter.types.key)
        } else {
          this.resetTopicFilter()
        }
      },
      onFiltersApplied(filters) {
        this.showFilters = false
        this.updateFilters(clone(filters))
        this.onFilter()
      },
      onFiltersReset() {
        this.resetFilter()
        this.updateNews()
      },
      moveButton() {
        const moveButton = setInterval(() => {
          const filterButton = document.querySelector('.filter-fab')
          if (filterButton) {
            const target = document.querySelector('.v-main')
            clearInterval(moveButton)
            if (target) {
              target.append(filterButton)
              filterButton.classList.remove('invisible')
            }
          }
        }, 200)
      },
      removeButton() {
        const filterButton = document.querySelector('.v-main > .filter-fab')

        if (filterButton) {
          filterButton.remove()
        }
      },
    },
  }
</script>
